/**
 * useMessageFilters - Hook for managing message list filter state
 */

import { useState, useCallback, useMemo } from 'react';
import { useUserDetailsContext } from 'context/userDetailsContext';
import { handleFilterUpdateInUserContext, handleFilterResetUserContext } from 'common/pagination';
import { convertToTimezoneFormat } from 'utils/utility_functions/timezone';
import { convertCaseToFilterFormat } from 'utils/utils';
import moment from 'moment';
import { PAGINATION } from '../constants/index';
import useEncryptedURLState from 'hooks/useEncryptedURLState';

/**
 * @param {Object} options
 * @param {boolean} options.isGlobal
 * @param {boolean} options.isFromClientPortal
 * @returns {Object} Filter state and handlers
 */
export function useMessageFilters({ isGlobal = false, isFromClientPortal = false } = {}) {
  const userContext = useUserDetailsContext();

  // ── Saved filter preferences (for global screen) ──────────────────────────
  const savedFilters = isGlobal ? userContext?.userDetails?.user_preferences?.filters?.secure_messages : null;

  // ── Helper Functions ──────────────────────────────────────────────────────
  const normalizeStatusValue = (value) => (value === 'All' ? '' : value || '');

  const getInitialCase = () => {
    if (isFromClientPortal) return convertCaseToFilterFormat(userContext?.clientSelectedCase);
    if (isGlobal) return savedFilters?.case || '';
    return '';
  };

  const getInitialDates = () => {
    if (isGlobal && Array.isArray(savedFilters?.date) && savedFilters.date.length === 2) {
      return savedFilters.date.map((d) => (d ? new Date(d) : null));
    }
    return [];
  };

  // ── Filter State (Single Object) ──────────────────────────────────────────
  const [filters, setFilters] = useState({
    case: getInitialCase(),
    author: isGlobal ? savedFilters?.author : null,
    date: getInitialDates(),
    status: isGlobal ? normalizeStatusValue(savedFilters?.message_status) : '',
    sentiment: isGlobal ? savedFilters?.sentiment : null,
    participant: isGlobal ? savedFilters?.participants : null,
    search: '',
  });

  // ── Pagination State (offset-based for RDS API) ───────────────────────────
  const [currentPage, setCurrentPage] = useEncryptedURLState('page', 1, { encrypt: false });
  const [pageSize, setPageSize] = useState(userContext?.userDetails?.pagination_preference?.messages?.self || PAGINATION.DEFAULT_PAGE_SIZE);

  // ── Helper: Extract Date Range ────────────────────────────────────────────
  const extractDateRange = useCallback(
    (dateArray) => {
      if (!dateArray?.[0]) return { date_from: '', date_to: '' };
      return {
        date_from: convertToTimezoneFormat(moment(dateArray[0]).startOf('day'), userContext?.userDetails?.timezone),
        date_to: convertToTimezoneFormat(moment(dateArray[1] || dateArray[0]).endOf('day'), userContext?.userDetails?.timezone),
      };
    },
    [userContext?.userDetails?.timezone]
  );

  // ── Helper: Persist Filter to User Preferences ────────────────────────────
  const persistFilter = useCallback(
    (key, value) => {
      if (isGlobal) {
        handleFilterUpdateInUserContext(userContext, 'filters', 'secure_messages', null, key, value, true);
      }
    },
    [isGlobal, userContext]
  );

  // ── Computed filter object for API ────────────────────────────────────────
  const apiFilters = useMemo(() => {
    const dateRange = extractDateRange(filters.date);
    return {
      case_id: typeof filters.case === 'object' ? filters.case?.id || '' : filters.case,
      author_id: filters.author,
      message_status: filters.status, // Handles both scheduled and read/unread
      keyword: filters.search,
      participants: filters.participant,
      sentiment: filters.sentiment,
      ...dateRange,
    };
  }, [filters]);

  const paginationState = useMemo(
    () => ({
      offset: (currentPage - 1) * pageSize,
      limit: pageSize,
      sort_order: 'desc',
    }),
    [currentPage, pageSize]
  );

  // ── Generic Filter Update Handler ─────────────────────────────────────────
  const updateFilter = useCallback(
    (key, value, persistKey = key) => {
      setFilters((prev) => ({ ...prev, [key]: value }));
      setCurrentPage(1); // Reset to first page on filter change

      if (persistKey) {
        persistFilter(persistKey, value);
      }
    },
    [persistFilter]
  );

  // ── Pagination Handlers ───────────────────────────────────────────────────
  const handlePageChange = useCallback((page) => {
    setCurrentPage(page);
  }, []);

  const handlePageSizeChange = useCallback((size) => {
    setPageSize(size);
    setCurrentPage(1); // Reset to first page when changing size
  }, []);

  // ── Reset All Filters ─────────────────────────────────────────────────────
  const resetFilters = useCallback(() => {
    const defaultCase = isFromClientPortal ? convertCaseToFilterFormat(userContext?.clientSelectedCase) : '';

    setFilters({
      case: defaultCase,
      author: null,
      date: [],
      status: '',
      sentiment: '',
      participant: '',
      search: '',
    });
    setCurrentPage(1);
    setPageSize(PAGINATION.DEFAULT_PAGE_SIZE);

    if (isGlobal) {
      handleFilterResetUserContext(
        userContext,
        'filters',
        'secure_messages',
        null,
        {
          date: null,
          message_status: null,
          sentiment: null,
          case: null,
          author: null,
          participants: null,
        },
        false
      );
    }
  }, [isFromClientPortal, isGlobal, userContext]);

  return {
    // Filter state
    filters,
    updateFilter,

    // Computed
    apiFilters,
    paginationState,

    // Pagination state
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,

    // Reset
    resetFilters,
  };
}
